<template>
    <div>
        <add-attendee-modal ref="addAttendeeModal" :field-groups="fieldGroups"
                            :attendees="attendees" :message-id="message.id"></add-attendee-modal>

        <h1 class="mb-8 font-bold text-3xl">
            <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('events.index')">Events
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('events.show', [eventInfo.id])">
                {{ eventInfo.name }}
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            <inertia-link class="text-indigo-400 hover:text-indigo-600"
                          :href="route('events.messages.index', [eventInfo.id])">
                Messages
            </inertia-link>
            <span class="text-indigo-400 font-medium">/</span>
            {{ message.name }}
        </h1>

        <div class="flex flex-row gap-4">
            <div class="w-full lg:w-1/2">
                <card>
                    <template slot="header">
                        Message Overview
                    </template>

                    <div class="p-8 flex flex-wrap">
                        <div class="pr-6 pb-4 w-full"><span class="font-semibold block">Name:</span>{{ message.name }}
                        </div>

                        <div class="pr-6 pb-4 w-full"><span
                            class="font-semibold block">Subject:</span>{{ message.subject }}
                        </div>

                        <div v-if="message.to_fields.length > 0" class="pr-6 pb-4 w-full">
                            <span class="font-semibold block">To:</span>
                            <ul class="list-disc list-inside">
                                <li v-for="field in message.to_fields" v-text="field"></li>
                            </ul>
                        </div>

                        <div v-if="message.cc_fields.length > 0" class="pr-6 pb-4 w-full">
                            <span class="font-semibold block">Cc:</span>
                            <ul class="list-disc list-inside">
                                <li v-for="field in message.cc_fields" v-text="field"></li>
                            </ul>
                        </div>

                        <div v-if="message.bcc_fields.length > 0" class="pr-6 pb-4 w-full">
                            <span class="font-semibold block">Bcc:</span>
                            <ul class="list-disc list-inside">
                                <li v-for="field in message.bcc_fields" v-text="field"></li>
                            </ul>
                        </div>

                        <div class="pr-6 pb-4 w-full"><span class="font-semibold block">Message:</span>
                            <div v-html="nl2br(message.message_body)"></div>
                        </div>

                        <div v-if="uploads.attachment_template" class="pr-6 pb-4 w-full">
                            <span class="font-semibold block">Attachment Template:</span>
                            <a class="text-indigo-400 hover:text-indigo-600"
                               :href="route('download-media.show', uploads.attachment_template.uuid)"
                               download>{{ uploads.attachment_template.file_name }}</a>
                        </div>
                    </div>

                    <template slot="footer">
                        <button class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">
                            Delete Message
                        </button>
                        <inertia-link class="btn-indigo ml-auto" :href="route('messages.edit', message.id)">
                            Edit Message
                        </inertia-link>
                    </template>
                </card>
            </div>

            <div class="w-full lg:w-1/2 px-2">
                <card>
                    <template slot="header">
                        Actions
                    </template>

                    <card-list-item :button="true" v-on:click="addAttendees()">
                        Add Attendees to Message
                    </card-list-item>

                    <card-list-item :button="true" v-on:click="generateDrafts()">
                        Generate Drafts
                    </card-list-item>

                    <card-list-item :button="true" v-on:click="sendMessage()">
                        Send All
                    </card-list-item>

                </card>
            </div>
        </div>

        <table-wrapper :headers="['Given Name', 'Surname', 'Email', 'Attachment', 'Status', 'Actions']"
                       :text-wrap="false" class="mb-4">
            <table-row v-for="recipient in recipients" :key="recipient.id">
                <table-cell>{{ recipient.given_name }}</table-cell>
                <table-cell>{{ recipient.surname }}</table-cell>
                <table-cell>{{ recipient.email }}</table-cell>
                <table-cell>
                    <a v-if="recipient.attachment_personalized"
                       class="text-indigo-400 hover:text-indigo-600"
                       :href="route('download-media.show', recipient.attachment_personalized.uuid)"
                       download>
                        {{ recipient.attachment_personalized.file_name }}
                    </a>
                </table-cell>
                <table-cell>
                    {{ statusName(recipient.status) }}
                </table-cell>
                <table-cell colspan="2">
                    <button @click="deleteRecipient(recipient.id)" class="btn-indigo-sm" tabindex="-1" type="button">
                        Delete
                    </button>
                    <button @click="sendMessage(recipient.id)" class="btn-indigo-sm ml-1" tabindex="-1" type="button">
                        Send
                    </button>
                </table-cell>
            </table-row>

            <table-row v-if="recipients.length === 0">
                <table-cell colspan="6">
                    No Recipients
                </table-cell>
            </table-row>
        </table-wrapper>

    </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import Card from '@/Shared/Card/Card'
import CardListItem from "@/Shared/Card/CardListItem.vue";
import TableWrapper from "@/Shared/Table/TableWrapper"
import TableRow from "@/Shared/Table/TableRow"
import TableCell from "@/Shared/Table/TableCell"
import TableCellEnd from "@/Shared/Table/TableCellEnd"
import {find} from "lodash";
import {RecipientStatusEnum} from "../../Enums/Enums";
import AddAttendeeModal from "./Partials/AddAttendeeModal.vue";

export default {
    metaInfo() {
        return {title: this.message.name}
    },
    components: {
        AddAttendeeModal,
        Card,
        CardListItem,
        TableWrapper,
        TableRow,
        TableCell,
        TableCellEnd,
    },
    props: {
        message: Object,
        eventInfo: Object,
        recipients: Array,
        fieldGroups: Array,
        uploads: Object,
        attendees: Array,
    },
    layout: Layout,
    methods: {
        destroy() {
            if (confirm('Are you sure you want to delete this message?')) {
                this.$inertia.delete(this.route('messages.destroy', this.message.id))
            }
        },
        deleteRecipient(id) {
            if (confirm('Are you sure you want to remove this recipient?')) {
                this.$inertia.delete(this.route('messages.recipients.destroy', [this.message.id, id]))
            }
        },
        addAttendees() {
            this.$refs.addAttendeeModal.show()
        },
        generateDrafts() {
            this.$inertia.post(this.route('messages.generate-draft', [this.message.id]))
        },
        sendMessage(recipient=null) {
            this.$inertia.post(this.route('messages.send', [this.message.id]), {
                'recipient': recipient
            })
        },
        statusName(status) {
            return find(RecipientStatusEnum, ['value', status])['label']
        },
        nl2br(str) {
            return str.replace(/(?:\r\n|\r|\n)/g, '<br>');
        }
    },
}
</script>
